import { EditorState, EditorSelection } from "@codemirror/state"
import { EditorView, keymap, placeholder, drawSelection, highlightActiveLine } from "@codemirror/view"
import { history, defaultKeymap, historyKeymap } from "@codemirror/commands"
import { autocompletion, completionKeymap, acceptCompletion } from "@codemirror/autocomplete"
import { markdown, insertNewlineContinueMarkup, deleteMarkupBackward } from "@codemirror/lang-markdown"
import { markdownStyling } from "./markdown"
import { mentionPills, mentionCompletions, followMention } from "./mentions"
import { embedCards, embedCompletions, openEmbedAtCursor } from "./embeds"
import { outlineFolding, toggleFoldAtCursor, toggleAllFolds } from "./folding"
import { slashCommands } from "./slash"

function toggleWrap(view, marker) {
  const changes = view.state.changeByRange((range) => {
    const text = view.state.sliceDoc(range.from, range.to)
    const before = view.state.sliceDoc(Math.max(0, range.from - marker.length), range.from)
    const after = view.state.sliceDoc(range.to, range.to + marker.length)

    if (before === marker && after === marker) {
      return {
        changes: [
          { from: range.from - marker.length, to: range.from },
          { from: range.to, to: range.to + marker.length }
        ],
        range: EditorSelection.range(range.from - marker.length, range.to - marker.length)
      }
    }
    return {
      changes: [
        { from: range.from, insert: marker },
        { from: range.to, insert: marker }
      ],
      range: EditorSelection.range(range.from + marker.length, range.to + marker.length)
    }
  })
  view.dispatch(changes, { userEvent: "input" })
  return true
}

const ootlineTheme = EditorView.theme(
  {
    "& .cm-tooltip": {
      backgroundColor: "color-mix(in srgb, var(--color-nord1) 96%, white)",
      border: "1px solid color-mix(in srgb, var(--color-nord3) 60%, transparent)",
      borderRadius: "0.5rem",
      boxShadow: "0 12px 32px rgb(0 0 0 / 0.45)",
      overflow: "hidden"
    },
    "& .cm-tooltip.cm-tooltip-autocomplete > ul": {
      fontFamily: "var(--font-sans)",
      fontSize: "0.875rem",
      maxHeight: "16rem"
    },
    "& .cm-tooltip.cm-tooltip-autocomplete > ul > li": {
      padding: "0.45rem 0.9rem",
      color: "var(--color-nord4)"
    },
    "& .cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]": {
      backgroundColor: "var(--color-nord2)",
      color: "var(--color-nord6)"
    },
    "& .cm-completionDetail": {
      float: "right",
      marginLeft: "1.5rem",
      fontStyle: "normal",
      fontSize: "0.7rem",
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      color: "color-mix(in srgb, var(--color-nord4) 55%, transparent)"
    },
    "& .cm-completionIcon": { display: "none" }
  },
  { dark: true }
)

export function createWritingEditor({ parent, doc, placeholderText, onDocChanged, onSaveRequested }) {
  const extensions = [
    ootlineTheme,
    history(),
    drawSelection(),
    highlightActiveLine(),
    markdown(),
    markdownStyling,
    mentionPills,
    embedCards,
    outlineFolding,
    autocompletion({ override: [slashCommands, embedCompletions, mentionCompletions], icons: false }),
    placeholder(placeholderText || "Start your story here… # for headings, / for commands, @ for people"),
    EditorView.contentAttributes.of({ spellcheck: "true", autocorrect: "on", autocapitalize: "on" }),
    keymap.of([
      { key: "Mod-s", run: () => (onSaveRequested?.(), true) },
      { key: "Tab", run: acceptCompletion },
      { key: "Enter", run: openEmbedAtCursor },
      { key: "Enter", run: insertNewlineContinueMarkup },
      { key: "Backspace", run: deleteMarkupBackward },
      { key: "Mod-Enter", run: (view) => followMention(view) || openEmbedAtCursor(view) },
      { key: "Mod-b", run: (view) => toggleWrap(view, "**") },
      { key: "Mod-i", run: (view) => toggleWrap(view, "*") },
      { key: "Mod-.", run: toggleFoldAtCursor },
      { key: "Mod-Shift-.", run: toggleAllFolds },
      ...completionKeymap,
      ...historyKeymap,
      ...defaultKeymap
    ]),
    EditorView.updateListener.of((update) => {
      if (update.docChanged) onDocChanged?.(update.view)
    })
  ]

  return new EditorView({
    parent,
    state: EditorState.create({ doc, extensions })
  })
}
